// src/data/reportsClient.js
//
// Read-only client for /api/reports/*. Three GETs, all cached for 5
// minutes via services/reportsCache.js. Cache is invalidated on every
// location/lease write through services/cacheBus.js (wired in the
// write paths of locationsClient + leasesClient).
//
// Per CONTRACT.md ("Portfolio reports"):
//   - viewer-readable
//   - no audit emission (reads)
//   - server already coerces NUMERIC strings to Number; client just
//     forwards the rows
//   - errors are typed instances from src/data/errors.js

import {
  AuthError,
  NetworkError,
  errorFromResponse,
} from "./errors.js";
import {
  KEY_LEASE_COUNTDOWN,
  KEY_OFFICE_CAPACITY,
  KEY_OFFICE_UTILIZATION,
  KEY_PREFIX_OFFICE_UTILIZATION_PORTFOLIO,
  KEY_HEADCOUNT_TREND,
  KEY_PREFIX_RTO_MONTHLY,
  KEY_PREFIX_RTO_QUARTERLY,
  KEY_PREFIX_RTO_WEEKLY,
  KEY_PREFIX_FOOT_TRAFFIC,
  KEY_COST_LEAKAGE,
  keyFor,
  get as cacheGet,
  set as cacheSet,
} from "../services/reportsCache.js";
import { log } from "../utils/logger.js";
import { getLocations } from "./locationsClient.js";
import {
  computeSpaceFit,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_DENSE,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
} from "../utils/spaceFitVerdict.js";
import { USABLE_CAPACITY_STAGES_SET } from "../utils/capacityStages.js";
import { getDisplayedSqft } from "../utils/locationSqftDerived.js";
import { dateOnly } from "../../shared/leaseAreaBasis.js";

const BASE = "/api/reports";
let activeAttendanceSource = "unknown";

/**
 * Fetch the office-local all-badge-ins report. The server owns the default
 * office and date range when filters are omitted.
 *
 * @param {{officeId?: string, from?: string, to?: string}} [filters]
 */
export async function getFootTrafficReport(filters = {}) {
  const params = new URLSearchParams();
  if (filters.officeId) params.set("officeId", filters.officeId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  const cacheKey = keyFor(KEY_PREFIX_FOOT_TRAFFIC, filters);
  const cached = cacheGet(cacheKey);
  if (cached) return cached;
  const suffix = params.toString();
  const data = await jsonRequest(`${BASE}/foot-traffic${suffix ? `?${suffix}` : ""}`);
  cacheSet(cacheKey, data);
  return data;
}

/** Fetch the annualized USD cost leakage report. */
export async function getCostLeakageReport() {
  const cached = cacheGet(KEY_COST_LEAKAGE);
  if (cached) return cached;
  const data = await jsonRequest(`${BASE}/cost-leakage`);
  cacheSet(KEY_COST_LEAKAGE, data);
  return data;
}

// ─── Internal: same fetch wrapper shape as locationsClient ────────────────

async function jsonRequest(url) {
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    log.error("reportsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("reportsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── Lease Countdown ──────────────────────────────────────────────────────

/**
 * @typedef  {Object} LeaseCountdownRow
 * @property {string}      locationId
 * @property {string}      market
 * @property {string|null} locationAlias
 * @property {string}      region
 * @property {string|null} activeLeaseId
 * @property {string|null} leaseEndDate     YYYY-MM-DD
 * @property {number|null} daysRemaining
 * @property {string|null} urgency          server-computed band string
 * @property {number|null} totalCost
 * @property {string|null} currency
 */

/**
 * @returns {Promise<LeaseCountdownRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getLeaseCountdownReport() {
  const cached = cacheGet(KEY_LEASE_COUNTDOWN);
  if (cached) {
    log.api("reportsClient.getLeaseCountdownReport cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest(`${BASE}/lease-countdown`);
  cacheSet(KEY_LEASE_COUNTDOWN, rows);
  log.api("reportsClient.getLeaseCountdownReport fetched", { count: rows.length });
  return rows;
}

// ─── Office Capacity ──────────────────────────────────────────────────────

/**
 * @typedef  {Object} OfficeCapacityRow
 * @property {string}      locationId
 * @property {string}      market
 * @property {string|null} locationAlias
 * @property {string}      region
 * @property {number|null} installedCapacity
 * @property {number|null} headcountTotal
 * @property {number|null} capacityRatio    headcount / installedCapacity, 2dp
 * @property {string|null} headcountAsOf    YYYY-MM-DD snapshot date
 */

/**
 * @typedef  {Object} OfficeCapacityPortfolioKpis
 * @property {number} totalAssigned        sum of headcount_total across all active offices
 * @property {number} unassignedHeadcount  workers with no OfficeSpace ID in the latest snapshot
 */

/**
 * @typedef  {Object} OfficeCapacityReport
 * @property {OfficeCapacityRow[]}          rows
 * @property {OfficeCapacityPortfolioKpis}  portfolioKpis
 */

/**
 * @returns {Promise<OfficeCapacityReport>}
 * @throws  {NetworkError | AuthError}
 */
export async function getOfficeCapacityReport() {
  const cached = cacheGet(KEY_OFFICE_CAPACITY);
  if (cached) {
    log.api("reportsClient.getOfficeCapacityReport cache hit", { count: cached.rows?.length ?? 0 });
    return cached;
  }
  const data = await jsonRequest(`${BASE}/office-capacity`);
  // Normalise: the server now returns { rows, portfolioKpis }. Guard against
  // old cached blobs (array shape) that may exist in a browser session before
  // the cache key version bump evicts them.
  const report = Array.isArray(data)
    ? { rows: data, portfolioKpis: { totalAssigned: 0, unassignedHeadcount: 0 } }
    : data;
  cacheSet(KEY_OFFICE_CAPACITY, report);
  log.api("reportsClient.getOfficeCapacityReport fetched", { count: report.rows?.length ?? 0 });
  return report;
}

// ─── Office Utilization ───────────────────────────────────────────────────

/**
 * @typedef  {Object} OfficeUtilizationRow
 * @property {string}      locationId
 * @property {string}      market
 * @property {string|null} locationAlias
 * @property {string}      region
 * @property {number|null} installedCapacity
 * @property {number|null} avgUtilization   integer
 * @property {number|null} peakUtilization  integer
 * @property {number|null} avgRatio         avg / installedCapacity, 2dp
 * @property {number|null} peakRatio        peak / installedCapacity, 2dp
 * @property {number}      periodCount      0-3 monthly samples averaged
 */

/**
 * @returns {Promise<OfficeUtilizationRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getOfficeUtilizationReport() {
  const cached = cacheGet(KEY_OFFICE_UTILIZATION);
  if (cached) {
    log.api("reportsClient.getOfficeUtilizationReport cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest(`${BASE}/office-utilization`);
  cacheSet(KEY_OFFICE_UTILIZATION, rows);
  log.api("reportsClient.getOfficeUtilizationReport fetched", { count: rows.length });
  return rows;
}

// ─── Office Utilization Portfolio ─────────────────────────────────────────────

/**
 * @typedef  {Object} PortfolioUtilizationRow
 * @property {string}      locationId
 * @property {string}      market
 * @property {string|null} locationAlias
 * @property {string}      region
 * @property {string|null} sourceSystem
 * @property {number|null} avgCount          rounded integer
 * @property {number|null} peakCount         integer
 * @property {number|null} installedCapacity
 * @property {number|null} peakUtilPct       0-1 ratio, 4dp
 * @property {number|null} avgUtilPct        0-1 ratio, 4dp
 * @property {number|null} assigned          latest headcount total
 * @property {number|null} sharingRatio      seats / headcount, 2dp
 * @property {number|null} surplusAtPeak     seats - peak (signed)
 */

/**
 * @typedef  {Object} PortfolioUtilizationResponse
 * @property {string|null}  month            resolved "YYYY-MM" string
 * @property {string[]}     availableMonths  all months with data, desc
 * @property {number}       readerlessCount  active locations without any reader
 * @property {PortfolioUtilizationRow[]} rows
 */

/**
 * Fetch the portfolio utilization ranking for a specific month.
 * When month is omitted the server defaults to MAX(month) in the dataset.
 *
 * @param {string} [month] - Optional "YYYY-MM" string.
 * @returns {Promise<PortfolioUtilizationResponse>}
 * @throws  {NetworkError | AuthError}
 */
export async function getOfficeUtilizationPortfolio(month) {
  const cacheKey = keyFor(KEY_PREFIX_OFFICE_UTILIZATION_PORTFOLIO, {
    month,
    attendanceSource: activeAttendanceSource,
  });
  const cached = cacheGet(cacheKey);
  if (cached) {
    log.api("reportsClient.getOfficeUtilizationPortfolio cache hit", { month, count: cached.rows?.length });
    return cached;
  }
  const url = month ? `${BASE}/office-utilization-portfolio?month=${encodeURIComponent(month)}` : `${BASE}/office-utilization-portfolio`;
  const data = await jsonRequest(url);
  activeAttendanceSource = data?.attendanceSourceMode ?? data?.attendanceSource ?? activeAttendanceSource;
  cacheSet(keyFor(KEY_PREFIX_OFFICE_UTILIZATION_PORTFOLIO, {
    month,
    attendanceSource: activeAttendanceSource,
  }), data);
  log.api("reportsClient.getOfficeUtilizationPortfolio fetched", { month: data.month, count: data.rows?.length });
  return data;
}

/**
 * Fetch the source-aware utilization report. The server owns aggregation,
 * sensitivity shaping, and the selected attendance source; the client cache
 * varies by every report input so controls never reuse stale data.
 *
 * @param {{month?: string, period?: "monthly"|"quarterly"|"weekly", region?: string}} [filters]
 */
export async function getUtilizationReport(filters = {}) {
  const params = new URLSearchParams();
  for (const key of ["month", "period", "region"]) {
    if (filters[key]) params.set(key, filters[key]);
  }
  // The server caches by period, region, role and live source mode. Do not
  // cache this response again in the browser: a source-flag change (or a
  // newly completed month) must be seen on the next request.
  const suffix = params.toString();
  const data = await jsonRequest(`${BASE}/utilization${suffix ? `?${suffix}` : ""}`);
  log.api("reportsClient.getUtilizationReport fetched", {
    month: data.month,
    period: data.period,
    offices: data.offices?.length ?? 0,
  });
  return data;
}

// ─── Data Coverage ────────────────────────────────────────────────────────────

// Home has its own effective-admin read boundary; report access stays unchanged.
// Never cache authorization-bearing freshness evidence in the client.
export function getHomeFreshness() {
  return jsonRequest(`${BASE}/data-coverage/home-freshness`);
}

// No client cache: role/governance and persisted evidence are rechecked on each
// mount/month selection, including after a user or permission change.
export function getDataCoverage({ month } = {}) {
  const params = new URLSearchParams();
  if (month) params.set("month", month);
  return jsonRequest(`${BASE}/data-coverage${params.size ? `?${params}` : ""}`);
}

export function getDataCoverageOffice({ month, officeId }) {
  const params = new URLSearchParams({ month, officeId });
  return jsonRequest(`${BASE}/data-coverage/office?${params}`);
}

// ─── Headcount Trend ──────────────────────────────────────────────────────────

/**
 * @typedef  {Object} HeadcountTrendAggRow
 * @property {string}  snapshotDate   YYYY-MM-DD
 * @property {number}  ft             Full-time headcount
 * @property {number}  cw             Vendor/contingent headcount
 * @property {number}  ic             Independent contractor headcount
 * @property {number}  total          ft + cw + ic (mapped offices only)
 * @property {number}  officeSum      Sum of total_count across all office rows
 * @property {number}  unassigned     Workers with no office mapping
 * @property {number}  totalRecords   officeSum + unassigned
 * @property {boolean} reconciles     true when headcount_portfolio_kpis row exists
 * @property {string}  appliedBy      Display name or email of the uploader
 */

/**
 * @typedef  {Object} HeadcountTrendSnapshot
 * @property {string}      snapshotDate
 * @property {number}      ft
 * @property {number}      cw
 * @property {number}      ic
 * @property {number}      total
 * @property {number|null} delta  change vs previous snapshot; null for first
 */

/**
 * @typedef  {Object} HeadcountTrendOffice
 * @property {string}                    locationId
 * @property {string}                    alias
 * @property {string}                    market
 * @property {string}                    region
 * @property {boolean}                   isActive
 * @property {HeadcountTrendSnapshot[]}  snapshots
 */

/**
 * @typedef  {Object} HeadcountTrendResponse
 * @property {HeadcountTrendAggRow[]}  aggregate
 * @property {HeadcountTrendOffice[]}  offices
 */

/**
 * @returns {Promise<HeadcountTrendResponse>}
 * @throws  {NetworkError | AuthError}
 */
export async function getHeadcountTrendReport() {
  const cached = cacheGet(KEY_HEADCOUNT_TREND);
  if (cached) {
    log.api("reportsClient.getHeadcountTrendReport cache hit", { snaps: cached.aggregate?.length });
    return cached;
  }
  const data = await jsonRequest(`${BASE}/headcount-trend`);
  cacheSet(KEY_HEADCOUNT_TREND, data);
  log.api("reportsClient.getHeadcountTrendReport fetched", { snaps: data.aggregate?.length, offices: data.offices?.length });
  return data;
}

export async function getRtoComplianceMonthly(month) {
  const cacheKey = keyFor(KEY_PREFIX_RTO_MONTHLY, { month });
  const cached = cacheGet(cacheKey);
  if (cached) return cached;
  const suffix = month ? `?month=${encodeURIComponent(month)}` : "";
  const data = await jsonRequest(`${BASE}/rto-compliance/monthly${suffix}`);
  cacheSet(cacheKey, data);
  return data;
}

export async function getRtoComplianceQuarterly(quarter) {
  const cacheKey = keyFor(KEY_PREFIX_RTO_QUARTERLY, { quarter });
  const cached = cacheGet(cacheKey);
  if (cached) return cached;
  const suffix = quarter ? `?quarter=${encodeURIComponent(quarter)}` : "";
  const data = await jsonRequest(`${BASE}/rto-compliance/quarterly${suffix}`);
  cacheSet(cacheKey, data);
  return data;
}

export async function getRtoComplianceWeekly(week) {
  const cacheKey = keyFor(KEY_PREFIX_RTO_WEEKLY, { week });
  const cached = cacheGet(cacheKey);
  if (cached) return cached;
  const suffix = week ? `?week=${encodeURIComponent(week)}` : "";
  const data = await jsonRequest(`${BASE}/rto-compliance/weekly${suffix}`);
  cacheSet(cacheKey, data);
  return data;
}

// ─── Space Fit Report ─────────────────────────────────────────────────────────

/**
 * @typedef {Object} SpaceFitOffice
 * @property {string}      id
 * @property {string}      market
 * @property {string|null} locationAlias
 * @property {string}      region
 * @property {string}      stage
 * @property {number|null} sf            Derived rentable SF
 * @property {number|null} seats         Installed capacity
 * @property {number|null} people        Latest headcount total
 * @property {number|null} sfPerSeat     sf / seats (null when sf or seats absent)
 * @property {string|null} densityBand   "roomy"|"comfortable"|"cramped"|null
 * @property {string|null} deskBand      "over"|"balanced"|"under"|null
 * @property {number|null} deskDelta     people - seats
 * @property {string|null} verdict       SPACE_FIT_VERDICT_* or null
 */

/**
 * @typedef {Object} SpaceFitKpis
 * @property {number} total     All operational and dormant offices
 * @property {number} assessed  Offices with a resolved verdict
 * @property {number} over      "Over capacity" count
 * @property {number} dense     "Dense" count
 * @property {number} under     "Under-used" count
 * @property {number} right     "Right-sized" count
 */

/**
 * @typedef {Object} SpaceFitReport
 * @property {SpaceFitOffice[]} offices
 * @property {SpaceFitKpis}     kpis
 */

/**
 * Build the Space Fit report client-side from the existing locations list.
 * No new API calls — reuses getLocations() (already cached by locationsClient).
 *
 * @returns {Promise<SpaceFitReport>}
 * @throws  {NetworkError | AuthError}
 */
export async function getSpaceFitReport() {
  const locations = await getLocations();
  const today = dateOnly(new Date());

  const offices = locations
    .filter((loc) => USABLE_CAPACITY_STAGES_SET.has(loc.stage) && loc.disposition !== "exit"
      && !(loc.exitDate && dateOnly(loc.exitDate) <= today
        && (!loc.reopenDate || dateOnly(loc.reopenDate) > today)))
    .map((loc) => {
      const sf    = getDisplayedSqft(loc, null);
      const seats = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
      const people = loc.latestHeadcount?.totalCount != null
        ? Number(loc.latestHeadcount.totalCount)
        : null;

      const { verdict, densityBand, deskBand, deskDelta } = computeSpaceFit(sf, seats, people);

      const sfPerSeat = sf != null && seats != null && seats > 0
        ? sf / seats
        : null;

      const maxCapacity = loc.maxCapacity != null ? Number(loc.maxCapacity) : null;
      const headroom = maxCapacity != null ? Math.max(0, maxCapacity - (seats ?? 0)) : null;

      return {
        id:               loc.id,
        market:           loc.market,
        locationAlias:    loc.locationAlias ?? null,
        region:           loc.region,
        stage:            loc.stage,
        isHub:            !!(loc.isHub),
        activeLeaseTypes: loc.activeLeaseTypes ?? [],
        sf,
        seats,
        people,
        sfPerSeat,
        densityBand,
        deskBand,
        deskDelta,
        verdict,
        headroom,
      };
    });

  const assessed = offices.filter((o) => o.verdict != null).length;
  const over     = offices.filter((o) => o.verdict === SPACE_FIT_VERDICT_OVER).length;
  const dense    = offices.filter((o) => o.verdict === SPACE_FIT_VERDICT_DENSE).length;
  const under    = offices.filter((o) => o.verdict === SPACE_FIT_VERDICT_UNDER).length;
  const right    = offices.filter((o) => o.verdict === SPACE_FIT_VERDICT_RIGHT).length;

  const kpis = { total: offices.length, assessed, over, dense, under, right };

  log.api("reportsClient.getSpaceFitReport built", {
    total: offices.length, assessed, over, dense, under, right,
  });
  return { offices, kpis };
}

// Re-export typed errors so callers only need to import from one place.
export { AuthError, NetworkError } from "./errors.js";
